import { formatToAED, formatToINR, formatToUSD } from '@/helpers/formatToINR'
import {
  useAddToWishlist,
  useRemoveFromWishlist,
  useWishlist,
} from '@/hooks/useWishlist'
import { Product } from '@/types/api'
import { easeInOut, motion } from 'framer-motion'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useState, useCallback, memo, useRef, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { Virtual, FreeMode, Pagination, Mousewheel } from 'swiper/modules'
import 'swiper/css'
import ButtonWithArrow from '../pages/home-page/home-page-widgets/ButtonWithArrow'
import { useCurrency } from './CurrencyProvider';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()

    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return isMobile
}

interface BorderConfig {
  enabled?: boolean
  color?: string
  width?: string
  position?: {
    top?: boolean
    bottom?: boolean
    between?: boolean
  }
}

interface ViewConfig {
  slidesPerView?: number | 'auto'
  spaceBetween?: number
}

interface ProductCardProps {
  data: Product
  isWishlisted: boolean
  onWishlistToggle: (id: number, event: React.MouseEvent) => void
  toastId: number | null
  className?: string
  cardClassName?: string
  mediaClassName?: string
  titleClassName?: string
  descriptionClassName?: string
  priceClassName?: string
  showWishlist?: boolean
  enableVideoHover?: boolean
  ctaConfig?: {
    text: string
    isEnable: boolean
  }
}

const ProductCard = memo(
  ({
    data,
    isWishlisted,
    onWishlistToggle,
    toastId,
    className = '',
    cardClassName = '',
    mediaClassName = 'h-[300px] md:h-[380px]',
    titleClassName = 'text-[12px] mb-sm:text-[12px] mb-md:text-[14px] mb-lg:text-[16px]',
    descriptionClassName = 'text-[10px] mb-sm:text-[14px]',
    priceClassName = 'text-[12px] mb-sm:text-[14px] mb-md:text-[16px] mb-lg:text-[18px]',
    showWishlist = true,
    enableVideoHover = true,
    ctaConfig,
  }: ProductCardProps) => {
    const router = useRouter()
    const [isHovered, setIsHovered] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const timeoutRef = useRef<NodeJS.Timeout>()
    const { currency } = useCurrency();
    const handleMouseEnter = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        if (!enableVideoHover) return

        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          setIsHovered(true)
          videoRef.current?.play().catch((error) => {
            console.log('Video play failed:', error)
          })
        }, 150)
      },
      [enableVideoHover]
    )

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        if (!enableVideoHover) return

        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          setIsHovered(false)
          if (videoRef.current) {
            videoRef.current.pause()
            videoRef.current.currentTime = 0
          }
        }, 150)
      },
      [enableVideoHover]
    )

    useEffect(() => {
      return () => {
        clearTimeout(timeoutRef.current)
      }
    }, [])

    const handleClick = useCallback(() => {
      router.push('/product-details/' + data.product_id)
    }, [router, data.product_id])

    const getFormattedPrice = (product:any) => {
      if (!product) return '';
      if (currency === 'AED') return formatToAED(+product?.dirhams_mrp);
      if (currency === 'USD') return formatToUSD(+product?.dollar_mrp);
       if (+product?.sp === 0) {
            return formatToINR(+product?.mrp);
          } else {
            return formatToINR(+product?.sp);
          }
    }

    return (
      <div className={`${cardClassName}`} onClick={handleClick}>
        <div className={`h-full flex flex-col ${className}`}>
          <div
            className='relative sm:h-[450px] h-auto w-full'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className={`relative w-full ${mediaClassName}`}>
              <div className='relative w-full h-full'>
                <img
                  src={
                    data.image_url_list && data.image_url_list?.length
                      ? data.image_url_list[0]?.url ?? ''
                      : ''
                  }
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                    isHovered && enableVideoHover ? 'opacity-0' : 'opacity-100'
                  }`}
                  alt={data.name}
                  loading='lazy'
                />
              </div>
              {enableVideoHover && data.video_url_list && (
                <video
                  ref={videoRef}
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}
                  muted
                  playsInline
                  loop
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <source src={data.video_url_list[0]?.url} type='video/mp4' />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            {showWishlist && (
              <div className='right-0 bottom-0 absolute m-4 cursor-pointer z-10'>
                <div className='relative'>
                  {toastId === data.product_id && (
                    <motion.div
                      initial={{ opacity: '0%' }}
                      animate={{ opacity: toastId != null ? '100%' : '0%' }}
                      transition={{ duration: 0.4, ease: easeInOut }}
                      className='absolute -top-10 -left-[75px]'
                    >
                      <div
                        className="font-barlowMedium z-30 text-black bg-white py-[5px] px-[10px] rounded-full text-[12px] tracking-[1.5px] relative
                      before:content-[''] before:absolute before:z-20 before:w-3 before:h-3 before:bg-white before:-bottom-[4px] transform before:rotate-45 before:right-[10px]"
                      >
                        WISHLISTED
                      </div>
                    </motion.div>
                  )}
                  <Image
                    height={400}
                    width={400}
                    alt='Heart icon'
                    src={
                      isWishlisted ? '/ic_heart_filled.png' : '/ic_heart.png'
                    }
                    className='object-contain h-[32px] w-[32px]'
                    onClick={(e) => onWishlistToggle(data.product_id, e)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className='flex flex-col flex-grow justify-between pt-[5px] px-[5px]'>
            <div>
              <div className='flex flex-row h-[16px] justify-start space-x-[6px] mb-2'>
                <Image
                  height={100}
                  width={100}
                  alt='Product image'
                  src='/ic_jewelery_mauve.svg'
                  className='w-[16px] h-[16px] p-0'
                />
                <Image
                  height={100}
                  width={100}
                  alt='Product image'
                  src='/ic_jewelery_gold.svg'
                  className='w-[16px] h-[16px] p-0'
                />
                <Image
                  height={100}
                  width={100}
                  alt='Product image'
                  src='/ic_jewelery_silver.svg'
                  className='w-[16px] h-[16px] p-0'
                />
              </div>
              <h1
                className={`font-barlowMedium text-[#D9D6C1] md:text-white line-clamp-2 group-hover:font-barlowSemibold duration-200 group-hover:underline tracking-wide mb-2 ${titleClassName}`}
              >
                {data.name}
              </h1>
              <p
                className={`line-clamp-2 text-[#E6DB96] md:text-[#EBE9DD] font-cambonExtraLight leading-[16px] mb-2 ${descriptionClassName}`}
              >
                {data.description}
              </p>
            </div>
            <h1
              className={`font-cambonLight group-hover:scale-125 duration-200 transform origin-left tracking-[1.5px] ${priceClassName}`}
            >
              {/* {formatToINR(+data.mrp)} */}
              {getFormattedPrice(data)}
            </h1>
            {ctaConfig?.isEnable && (
              <div className='self-left mt-2'>
                <ButtonWithArrow
                  title={ctaConfig.text}
                  minSize={24}
                  onClick={() =>
                    router.push(
                      `/product-listing?subcategory=Designer&main_category=Rings`
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)

ProductCard.displayName = 'ProductCard'

interface ProductGridProps {
  productList: Product[]
  isLoading?: boolean
  className?: string
  gridClassName?: string
  cardClassName?: string
  mediaClassName?: string
  titleClassName?: string
  descriptionClassName?: string
  priceClassName?: string
  withScroll?: boolean
  showWishlist?: boolean
  enableVideoHover?: boolean
  desktopConfig?: ViewConfig
  mobileConfig?: ViewConfig
  showPagination?: boolean
  borderConfig?: {
    desktop?: BorderConfig
    mobile?: BorderConfig
  }
  ctaConfig?: {
    text: string
    isEnable: boolean
  }
}

const ProductGridGlobal = ({
  productList,
  isLoading,
  className = '',
  gridClassName = '',
  cardClassName = '',
  mediaClassName = '',
  titleClassName = '',
  descriptionClassName = '',
  priceClassName = '',
  withScroll = false,
  showWishlist = true,
  enableVideoHover = true,
  desktopConfig = {
    slidesPerView: 'auto',
    spaceBetween: 20,
  },
  mobileConfig = {
    slidesPerView: 1,
    spaceBetween: 10,
  },
  showPagination = false,
  borderConfig = {
    desktop: {
      enabled: false,
      color: 'loveAffair',
      width: '0.2px',
      position: {
        top: false,
        bottom: false,
        between: false,
      },
    },
    mobile: {
      enabled: false,
      color: 'loveAffair',
      width: '0.2px',
      position: {
        top: false,
        bottom: false,
        between: false,
      },
    },
  },
  ctaConfig = {
    text: 'Shop Seattle Ring',
    isEnable: false,
  },
}: ProductGridProps) => {
  const isMobile = useIsMobile()
  const [optimisticWishlist, setOptimisticWishlist] = useState<number[]>([])
  const [toastId, setToastId] = useState<number | null>(null)
  const { data: wishListItems } = useWishlist()
  const { mutate: addToWishlist } = useAddToWishlist()
  const { mutate: removeFromWishlist } = useRemoveFromWishlist()
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)

  const currentBorderConfig = isMobile
    ? borderConfig.mobile
    : borderConfig.desktop

  useEffect(() => {
    if (wishListItems?.data) {
      setOptimisticWishlist(wishListItems.data.map((item) => item.product_id))
    }
  }, [wishListItems])

  const handleWishlistToggle = useCallback(
    (id: number, event: React.MouseEvent) => {
      event.stopPropagation()
      const isCurrentlyWishlisted = optimisticWishlist.includes(id)
      const wishlistItem = wishListItems?.data.find(
        (wl) => wl.product_id === id
      )

      if (isCurrentlyWishlisted) {
        setOptimisticWishlist((prev) => prev.filter((itemId) => itemId !== id))
      } else {
        setOptimisticWishlist((prev) => [...prev, id])
        setToastId(id)
        setTimeout(() => setToastId(null), 1500)
      }

      if (isCurrentlyWishlisted && wishlistItem) {
        removeFromWishlist(wishlistItem.wishlist_id, {
          onError: () => {
            setOptimisticWishlist((prev) => [...prev, id])
          },
        })
      } else {
        addToWishlist(
          {
            product_id: id,
          },
          {
            onError: () => {
              setOptimisticWishlist((prev) =>
                prev.filter((itemId) => itemId !== id)
              )
              setToastId(null)
            },
          }
        )
      }
    },
    [optimisticWishlist, wishListItems?.data, removeFromWishlist, addToWishlist]
  )

  const getBorderClasses = (isScrollView: boolean = false) => {
    if (!currentBorderConfig?.enabled) return ''

    const classes = []

    if (currentBorderConfig?.enabled) {
      const { color, position } = currentBorderConfig

      if (position?.top) {
        classes.push(isMobile ? 'border-t' : 'md:border-t')
      }
      if (position?.bottom) {
        classes.push(isMobile ? 'border-b' : 'md:border-b')
      }
      if (position?.between && !isScrollView) {
        classes.push(isMobile ? 'divide-y' : 'md:divide-x md:divide-y-0')
      }
      if (color) {
        classes.push(
          isMobile
            ? `border-${color} divide-${color}`
            : `md:border-${color} md:divide-${color}`
        )
      }
    }

    return classes.join(' ')
  }

  if (withScroll) {
    const currentConfig = isMobile ? mobileConfig : desktopConfig

    const swiperConfig = {
      modules: [
        Mousewheel,
        Virtual,
        FreeMode,
        ...(showPagination ? [Pagination] : []),
      ],
      slidesPerView: currentConfig.slidesPerView,
      spaceBetween: currentConfig.spaceBetween,
      freeMode: true,
      virtual: true,
      ...(showPagination && {
        pagination: {
          clickable: true,
          dynamicBullets: true,
        },
      }),
    }

    const scrollLeft = () => {
      if (swiperRef) {
        swiperRef.slidePrev()
      }
    }

    const scrollRight = () => {
      if (swiperRef) {
        swiperRef.slideNext()
      }
    }

    return (
      <div className={`relative ${gridClassName} ${getBorderClasses(true)}`}>
        <Swiper
          mousewheel={{ forceToAxis: true }}
          onSwiper={setSwiperRef}
          {...swiperConfig}
        >
          {productList?.map((item, index) => (
            <SwiperSlide
              key={item.product_id}
              virtualIndex={index}
              className={
                currentBorderConfig?.enabled
                  ? `border-r last:border-r-0 border-${currentBorderConfig.color}`
                  : ''
              }
            >
              <ProductCard
                data={item}
                isWishlisted={optimisticWishlist.includes(item.product_id)}
                onWishlistToggle={handleWishlistToggle}
                toastId={toastId}
                className={className}
                cardClassName={cardClassName}
                mediaClassName={mediaClassName}
                titleClassName={titleClassName}
                descriptionClassName={descriptionClassName}
                priceClassName={priceClassName}
                showWishlist={showWishlist}
                enableVideoHover={enableVideoHover}
                ctaConfig={ctaConfig}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {productList?.length ? (
          <div className='relative z-60 py-[5px] hidden md:flex gap-[20px]'>
            <div onClick={scrollLeft} className='cursor-pointer'>
              <Image
                alt='left'
                src={'/designer-ring-left.svg'}
                width={54}
                height={54}
              />
            </div>
            <div onClick={scrollRight} className='cursor-pointer'>
              <Image
                alt='left'
                src={'/designer-ring-right.svg'}
                width={54}
                height={54}
              />
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    )
  }

  return (
    <div
      className={`relative grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 ${getBorderClasses()} ${gridClassName}`}
    >
      {productList?.map((item, index) => (
        <ProductCard
          data={item}
          isWishlisted={optimisticWishlist.includes(item.product_id)}
          onWishlistToggle={handleWishlistToggle}
          toastId={toastId}
          className={className}
          cardClassName={cardClassName}
          mediaClassName={mediaClassName}
          titleClassName={titleClassName}
          descriptionClassName={descriptionClassName}
          priceClassName={priceClassName}
          showWishlist={showWishlist}
          enableVideoHover={enableVideoHover}
          key={index}
        />
      ))}
    </div>
  )
}

export default ProductGridGlobal
